import {useState} from "react"
import {Modal, Button} from "react-bootstrap"
import { wrappedGet } from "../../utilities/AxiosHelper"

const HELP_URL = "https://help.oars.umm.edu/"
const CLIENT_IP_URL = "/client-ip"

function normalizeIpAddress(ipAddress = "") {
    let normalizedIp = ipAddress.trim().toLowerCase()

    // Handle IPv4-mapped IPv6 addresses, such as ::ffff:192.168.1.10
    if (normalizedIp.startsWith("::ffff:")) {
        normalizedIp = normalizedIp.substring(7)
    }

    // Remove brackets from addresses such as [::1]
    if (normalizedIp.startsWith("[") && normalizedIp.endsWith("]")) {
        normalizedIp = normalizedIp.slice(1, -1)
    }

    // Remove an IPv6 zone identifier, such as fe80::1%eth0
    return normalizedIp.split("%")[0]
}

function isPrivateIpAddress(ipAddress) {
    const ip = normalizeIpAddress(ipAddress)

    if (!ip) {
        return false
    }

    // IPv6 loopback, unique-local, and link-local addresses
    if (
        ip === "::1" ||
        ip.startsWith("fc") ||
        ip.startsWith("fd") ||
        /^fe[89ab]/i.test(ip)
    ) {
        return true
    }

    const octets = ip.split(".").map(Number)

    if (
        octets.length !== 4 ||
        octets.some(
            (octet) =>
                !Number.isInteger(octet) ||
                octet < 0 ||
                octet > 255,
        )
    ) {
        return false
    }

    const [first, second] = octets

    return (
        first === 10 ||
        (first === 172 && second >= 16 && second <= 31) ||
        (first === 192 && second === 168) ||
        first === 127 ||
        (first === 169 && second === 254)
    )
}

export default function HelpLinkCheck() {
    // this is a stop/gap measure and will not work on localhost due to cors error  
    const [showVpnModal, setShowVpnModal] = useState(false)
    const [checking, setChecking] = useState(false)

    const handleClick = async (event) => {
        event.preventDefault()

        if (checking) {
            return
        }

        setChecking(true)

        try {
            const controller = new AbortController()
            const timeoutId = window.setTimeout(() => {
                controller.abort()
            }, 5000)

            try {
                const response = await wrappedGet(CLIENT_IP_URL)
                if (response.status !== 200) {
                    throw new Error(
                        `Unable to determine client IP: ${response.status}`,
                    )
                }

                const clientIp = response?.data?.ip ?? null
                if (!isPrivateIpAddress(clientIp)) {
                    setShowVpnModal(true)
                    return
                }

                window.open(
                    HELP_URL,
                    "_blank",
                    "noopener,noreferrer",
                )
            } finally {
                window.clearTimeout(timeoutId)
            }
        } catch (error) {
            console.warn("Unable to determine the client IP address:", error)

            // Fail closed so Help is not opened when the IP check fails.
            setShowVpnModal(true)
        } finally {
            setChecking(false)
        }
    }

    return (
        <>
            <a onClick={handleClick} className="menu-link px-5">
                <span className="menu-icon">
                    <i className="fas fa-external-link-square-alt fs-2 text-primary"></i>
                </span>
                <span>{checking ? "Checking Help..." : "Help & Support Home"}</span>
            </a>
            <Modal
                show={showVpnModal}
                onHide={() => setShowVpnModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>OARS Help Unavailable</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    OARS Help is currently only available while connected
                    to the VPN. Please connect to the VPN and try again.
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={() => setShowVpnModal(false)}
                    >
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
